import clsx from 'clsx'
import Icon from 'components/core-v2/atoms/Icon'
import { Calendar as ModuleCalendar } from 'react-multi-date-picker'

import { BodyProps, Value } from '.'
import classes from './index.module.scss'

type Props<T extends Value> = Omit<BodyProps<T>, 'id' | 'showClearDatesButton' | 'defaultValue'>

export const DayCalendar = <T extends Value>({
  maxSelectableDate,
  onChange,
  value,
  variant,
  ...calendarProps
}: Props<T>) => (
  <ModuleCalendar
    disableMonthPicker
    disableYearPicker
    rangeHover
    className={classes.calendar}
    maxDate={maxSelectableDate}
    numberOfMonths={2}
    range={variant === 'range'}
    renderButton={(direction, handleClick, disabled) => (
      <button className={classes.arrow} disabled={disabled} type="button" onClick={handleClick}>
        {direction === 'left' ? <Icon name="chevronLeft" /> : <Icon name="chevronRight" />}
      </button>
    )}
    value={value}
    weekStartDayIndex={1}
    onChange={date => {
      if (Array.isArray(date)) {
        if (date.length === 2) onChange(date)
      } else {
        onChange(new Date(date.toString()))
      }
    }}
    {...calendarProps}
  />
)

// The TypeScript ignores below are due to incorrect typing by the package author
const MonthCalendar = <T extends Value>({
  maxSelectableDate,
  onChange,
  value,
  ...calendarProps
}: Props<T>) => (
  <ModuleCalendar
    disableYearPicker
    hideMonth
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    isOnlyMonthPicker
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    onlyMonthPicker
    range
    rangeHover
    className={clsx(classes.calendar, classes.monthPicker)}
    maxDate={maxSelectableDate}
    months={['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']}
    renderButton={(direction, handleClick, disabled) => (
      <button className={classes.arrow} disabled={disabled} type="button" onClick={handleClick}>
        {direction === 'left' ? <Icon name="chevronLeft" /> : <Icon name="chevronRight" />}
      </button>
    )}
    value={value}
    onChange={date => {
      if (Array.isArray(date) && date.length === 2) {
        onChange([date[0].toFirstOfMonth(), date[1].toLastOfMonth()])
      }
    }}
    {...calendarProps}
  />
)

export const Calendar = <T extends Value>({ isOnlyMonthPicker, ...props }: Props<T>) =>
  isOnlyMonthPicker ? <MonthCalendar<T> {...props} /> : <DayCalendar<T> {...props} />
